import { Text, useToast, useModal } from '@afterpaytouch/core'
import { unwrapResult } from '@reduxjs/toolkit'
import { useTranslation } from 'next-i18next'
import React, { useContext, useEffect } from 'react'
import {
  DisabledModal,
  Head,
  BillingAgreementMessage,
  Donation,
  ShippingSummary,
  OrderSummary,
  OAuthLoginConsentModal,
  PaymentOptionsErrorModal,
  ConsumerLendingErrorModal,
  BiometricRegistrationModal,
  SummaryCtaFooter,
  PPAModal,
  VerticalPaymentSchedule,
  CrossBorderHeader,
  SupHeader,
  GiftCardHeader,
  SectionHeader,
  SummaryNotificationContext,
  AutopayModal,
} from '../../component'
import { Route } from '../../config/router'
import { useFlag } from '@afterpaytouch/feature'
import {
  ApplePayStatus,
  CheckoutStatus,
  PaymentMethodType,
  getCheckpoint,
  Features,
  getIdentityVerificationRoute,
  getPayIn2Experiment,
  CheckoutPage,
  CreateDocumentsRequest,
  BiometricRegistrationModalKey,
  IdentityVerificationModalKey,
  AFFAutopayConfirmationModalKey,
  AddressModeType,
} from '../../model'
import { PaymentMethodTypes, TrackingEvent } from '../../model/monitoring'
import {
  confirmCheckout,
  selectPurchaseMethod,
  setCardScanCheckpoint,
  useAppDispatch,
  useAppRouter,
  useApplePay,
  useApplePayState,
  useCheckoutError,
  useCheckoutState,
  useIsExpress,
  useIsReadyToConfirm,
  useIsShippingOptionValid,
  useSelectedPaymentMethod,
  useIsTrustedCheckout,
  useCountry,
  useShouldShowCashAppTooltip,
  useIsBiometricConflictingModal,
  useIsEligibleBiometricRegistration,
  setDisplayProcessingModal,
  useOAuthAuthorizationFlow,
  useExperiment,
  shouldShowOAuthConsentForm,
  acceptOAuthConsent,
  declineOAuthConsent,
  useCLRiskDeclineError,
  setStartConfirmTime,
  setEndConfirmTime,
  useHasSavedPaymentMethods,
  resetCheckoutError,
  useCheckoutToken,
  useIsUpdatePreferredPaymentDay,
  useCohort,
  useIsShippingAddressValid,
  useShippingAddress,
  useIsAfterpayFinanceFeeEligible,
  useShowAffIdvModal,
  useIsAfterpayAppGiftCardFlow,
  useIsPickup,
  createDocuments,
  usePreferredCard,
  useInitialData,
} from '../../state'
import { isPortalApiError } from '@afterpaytouch/portal-api/utils'
import { useCheckoutSizes } from '../../utils/responsive'
import { create } from 'react-modal-promise'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { usePostCheckoutTriggerExperiment } from '../../utils/post-checkout'
import { getConfirmCheckoutErrorHandling } from '../../utils/redirects'
import { Cohort, ErrorCode, FirstPaymentType } from '@afterpaytouch/portal-api'
import { sendPurchaseEvent } from '../../integrations/googletagmanager/utils'
import { IdentityVerificationModal } from '../../component/IdentityVerificationModal'
import { useGetIsAutoPayAgreed } from '../../component/AutopayToggle/hooks'

export const ArcadeSummaryPage: CheckoutPage = () => {
  const { notification, setClicked, clicked } = useContext(SummaryNotificationContext)
  const { t } = useTranslation()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const country = useCountry()
  const { status, firstPaymentType, started, checkout, error } = useCheckoutState()
  const isExpress = useIsExpress()
  const isPickup = useIsPickup()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const selectedPaymentMethod = useSelectedPaymentMethod()
  const { card } = usePreferredCard() ?? {}
  const applepay = useApplePayState()
  const ready = useIsReadyToConfirm()
  const applePayHelper = useApplePay()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const checkoutError = useCheckoutError()
  const shouldUpdatePreferredPaymentDay = useIsUpdatePreferredPaymentDay()
  const { addressMode } = useInitialData()

  const { isError: isPclError } = useCLRiskDeclineError()
  const isShippingOptionValid = useIsShippingOptionValid()
  const isShippingAddressValid = useIsShippingAddressValid()
  const { isCheckoutUpdated } = useShippingAddress()

  const { shouldShowCashAppAccountLinkingToast, saveToTags } = useShouldShowCashAppTooltip()
  const cashTooltipExperiment = useExperiment('CheckoutSummaryCashTooltip')
  const hasSavedPaymentMethods = useHasSavedPaymentMethods()

  // IDV
  const isSupIdvModalEnabled = useShowAffIdvModal()
  const { showModal: showSupIdvModal, closeModal: closeSupIdvModal } = useModal(IdentityVerificationModalKey)

  // biometrics
  const isConflictingModalsWithBiometric = useIsBiometricConflictingModal()
  const isEligibleBiometricRegistration = useIsEligibleBiometricRegistration()
  const { showModal: showBiometricModal, closeModal: closeBiometricModal } = useModal(BiometricRegistrationModalKey)

  const isAFFSUPFlow = isAFF && !isAfterpayAppGiftCardFlow
  const isAFFGiftCardFlow = isAFF && isAfterpayAppGiftCardFlow

  const cohort = useCohort()
  const isCashOnlyCohort = cohort === Cohort.CashOnly
  const OAuthEnabledForUnicorn = useFlag(Features.OAUTH_FOR_UNICORN, false)
  const OAuthEnabledForCohort = (isCashOnlyCohort && OAuthEnabledForUnicorn) || !isCashOnlyCohort

  // autopay for AFF
  const autoPayAgreed = useGetIsAutoPayAgreed() ?? false
  const {
    visible: autopayConfirmationModalVisible,
    showModal: showAutopayConfirmationModal,
    closeModal: closeAutopayConfirmationModal,
  } = useModal(AFFAutopayConfirmationModalKey)

  const handleAutopayConfirmationModalStatusChange = (value: boolean): void => {
    if (value) {
      setClicked(true)
      logEvent(TrackingEvent.PRESSED_AUTOPAY_ON_PAGE_CONFIRM_BUTTON)
      handleConfirm()
    }
  }

  useEffect(() => {
    try {
      const request: CreateDocumentsRequest = {
        autopay: autoPayAgreed,
      }
      dispatch(createDocuments(request))
    } catch (e) {
      router.push(Route.TERMINAL_ERROR)
    }
  }, [])

  // post checkout trigger experiment
  usePostCheckoutTriggerExperiment()

  const isSummaryButtonDisabled = (!ready || clicked || typeof selectedPaymentMethod === 'undefined' || notification?.severe) ?? false

  // PayIn2 experiment
  const payIn2ExperimentValue = useFlag(Features.CHECKOUT_PAY_IN_2_AMOUNT, -1)
  useExperiment('PayIn2Experiment', !isExpress, getPayIn2Experiment(payIn2ExperimentValue))

  const toast = useToast()

  useEffect(() => {
    const switchedToValidCard = selectedPaymentMethod?.type !== PaymentMethodType.CREDIT_CARD && card !== null && card?.error === null
    if (isAFF && switchedToValidCard) {
      dispatch(selectPurchaseMethod({ type: PaymentMethodType.CREDIT_CARD, card: { token: card.token } }, false))
    }
  }, [isAFF])

  useEffect(() => {
    if (typeof error !== 'undefined' && typeof error.extra !== 'undefined') {
      const { extra } = error
      if (extra.isEFAFinished) {
        dispatch(resetCheckoutError())
      }
    }
  }, [error])

  useEffect(() => {
    if (shouldShowCashAppAccountLinkingToast) {
      toast({
        message: () => (
          <>
            <Text size={textSize} bold>
              {t('common:firstPaymentWithCash.heading')}
            </Text>
            <Text size={textSize}>
              {t(cashTooltipExperiment.control ? 'common:firstPaymentWithCash.content' : 'common:firstPaymentWithCash.contentExperiment')}
            </Text>
          </>
        ),
        kind: 'Success',
        toastTime: 5000,
        swipeToDismiss: true,
      })
      saveToTags()
    }
  }, [shouldShowCashAppAccountLinkingToast, saveToTags])

  useEffect(() => {
    if (isSupIdvModalEnabled && status < CheckoutStatus.Confirming) {
      showSupIdvModal()
    } else {
      closeSupIdvModal()
    }
  }, [isSupIdvModalEnabled, showSupIdvModal])

  const { textSize } = useCheckoutSizes()
  const isTrusted = useIsTrustedCheckout()

  // OAuth
  const { oAuthAuthorization } = useOAuthAuthorizationFlow()
  const showPersistentLoginConsentForm = create(({ isOpen, onResolve, onReject }) => (
    <OAuthLoginConsentModal show={isOpen} onDecline={onReject} onAccept={onResolve} />
  ))

  // This is purely for analytics and should not be used elsewhere. The FE needs to differentiate between no downpayment and some downpayment but amplitude does not care.
  const analyticsFirstPaymentType = typeof firstPaymentType !== 'undefined' && firstPaymentType !== '' ? firstPaymentType : FirstPaymentType.PaymentUpfront
  const token = useCheckoutToken()

  const finaliseCheckout = async (): Promise<void> => {
    try {
      const confirmResponse = unwrapResult(await dispatch(confirmCheckout({ shouldUpdatePreferredPaymentDay })))

      await oAuthAuthorization(isTrusted)
      dispatch(setEndConfirmTime(performance.now()))

      if (confirmResponse.status !== 'FAILURE') {
        const orderAmount = started?.consumerAmount ?? checkout?.amount
        sendPurchaseEvent(token, !hasSavedPaymentMethods, orderAmount)

        dispatch(setDisplayProcessingModal(true))
      } else {
        router.push(Route.TERMINAL_ERROR)
      }
    } catch (e) {
      if (isPortalApiError(e)) {
        const { route, messageKey } = getConfirmCheckoutErrorHandling({
          errorCode: e.errorCode,
          returnRoute: Route.ENTER_PASSWORD,
        })
        if (Route.CARD_SCAN === route) {
          dispatch(setCardScanCheckpoint(getCheckpoint(e.message)))
        }
        if (typeof messageKey === 'string') {
          toast({
            message: t(messageKey),
            kind: 'Failure',
          })
          dispatch(resetCheckoutError())
        }

        if (route) {
          setClicked(false)
          router.push(route ?? Route.TERMINAL_ERROR)
        }
      } else {
        capture.warning('Unexpected error during payment confirmation', { ...e })
      }
    }
  }

  const handleConfirmClick = async (): Promise<void> => {
    if (isSummaryButtonDisabled) {
      logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_CONFIRM_BUTTON_DISABLED)
      return
    }

    if (isAFF && autoPayAgreed) {
      showAutopayConfirmationModal()
      return
    }

    setClicked(true)
    logEvent(TrackingEvent.PRESSED_SUMMARY_CONFIRM_BUTTON, {
      paymentMethodType: PaymentMethodTypes[selectedPaymentMethod?.type as PaymentMethodType],
    })
    handleConfirm()
  }

  const handleConfirm = async (): Promise<void> => {
    dispatch(setStartConfirmTime(performance.now()))

    await dispatch(selectPurchaseMethod(selectedPaymentMethod))

    if (selectedPaymentMethod?.type === PaymentMethodType.APPLE_PAY) {
      applePayHelper.initialise(t, logEvent)
      try {
        await applePayHelper.startSession()
      } catch (e) {
        return
      }
    }

    await finaliseCheckout()
  }

  const pageTitle = `${t('summary:reviewOrder')} | ${t('common:brandName')}`

  useEffect(() => {
    if (applepay.status === ApplePayStatus.Cancelled) {
      setClicked(false)
    }
  }, [applepay.status])

  useEffect(() => {
    if (status >= CheckoutStatus.Started && !ready) {
      logEvent(TrackingEvent.CHECKOUT_CONFIRM_DISABLED, {
        checkoutStatus: status,
        checkoutError: checkoutError?.error,
        isShippingOptionValid,
        isShippingAddressValid,
        isCheckoutUpdated,
      })
    }
  }, [ready])

  useEffect(() => {
    if (isConflictingModalsWithBiometric) {
      closeBiometricModal()
    }
  }, [isConflictingModalsWithBiometric])

  useEffect(() => {
    if (status <= CheckoutStatus.Loaded) {
      router.push(Route.LOGGED_IN)
    }

    // TODO: do we need to handle CheckoutStatus.Starting
    // Route to verify-identity if back button is clicked on [document]
    if (status === CheckoutStatus.IdentityIneligible) {
      logEvent(TrackingEvent.TRIGGER_IDENTITY_REDIRECT_SUMMARY_PAGE)
      const url = getIdentityVerificationRoute(country, {}, error?.errorCode as ErrorCode) as Route
      router.push(url)
    }

    if (status === CheckoutStatus.LackOfEligiblePaymentMethod) {
      router.push(Route.ADD_PAYMENT_METHOD)
    }

    router.prefetch(Route.PAYMENT_METHOD)
    router.prefetch(Route.NEW_PAYMENT_METHOD)
  }, [])

  useEffect(() => {
    const showOauthConsent = async (): Promise<void> => {
      const shouldShowOAuthConsent = await dispatch(shouldShowOAuthConsentForm)
      const shouldShowOauthConsentModal = shouldShowOAuthConsent && OAuthEnabledForCohort
      await (shouldShowOauthConsentModal
        ? showPersistentLoginConsentForm()
            .then(() => {
              dispatch(acceptOAuthConsent)
            })
            .catch(() => {
              dispatch(declineOAuthConsent)
            })
        : Promise.resolve())
    }
    showOauthConsent()
  }, [])

  useEffect(() => {
    if (isEligibleBiometricRegistration) {
      showBiometricModal()
    }
  }, [isEligibleBiometricRegistration])

  // Reset clicked state when autopay modal is closed
  useEffect(() => {
    if (!autopayConfirmationModalVisible && clicked) {
      setClicked(false)
    }
  }, [autopayConfirmationModalVisible, clicked, setClicked])
  return (
    <>
      <BiometricRegistrationModal />
      {isPclError && <ConsumerLendingErrorModal isError={isPclError} />}
      {status !== CheckoutStatus.Error && (
        <>
          <Head title={pageTitle} />
          <PaymentOptionsErrorModal />
          {status >= CheckoutStatus.Started && (
            <>
              <CrossBorderHeader />
              {isAFFSUPFlow && <SupHeader />}
              {isAFFGiftCardFlow && <GiftCardHeader />}
              {!isAFFGiftCardFlow && <OrderSummary narrowMargin={true} />}
              {isExpress && addressMode !== AddressModeType.NO_ADDRESS && (
                <>
                  <SectionHeader title={isPickup ? t('summary:pickup') : t('summary:delivery')} />
                  <ShippingSummary />
                </>
              )}
              <VerticalPaymentSchedule testNameSpace='vertical-payment-schedule' />
              <BillingAgreementMessage />
              <PPAModal />
              <Donation />
              <SummaryCtaFooter
                clicked={clicked}
                isSummaryButtonDisabled={isSummaryButtonDisabled}
                notification={notification}
                onConfirm={handleConfirmClick}
              />
              <TrackPageViewed eventProperties={{ firstPaymentType: analyticsFirstPaymentType, summaryPageVersion: 'ga launch v2' }} />
              {clicked && <DisabledModal />}
              <IdentityVerificationModal setClicked={setClicked} />
              <AutopayModal
                modalKey={AFFAutopayConfirmationModalKey}
                show={autopayConfirmationModalVisible}
                onCloseModal={closeAutopayConfirmationModal}
                onAutopayStatusChange={handleAutopayConfirmationModalStatusChange}
                autoPayAgreed={false}
              />
            </>
          )}
        </>
      )}
    </>
  )
}
